import { useCallback, useEffect, useMemo, useState } from 'react';

import { REWIND_SLIDES, RewindSlideId } from '../rewindModal.types';

type UseRewindSlidesReturn = {
  currentSlideIndex: number;
  currentSlideId: RewindSlideId;
  progress: number;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  isPaused: boolean;
  completedSlides: Set<RewindSlideId>;
  goToSlide: (index: number, progressPercent?: number) => void;
  goToNextSlide: () => void;
  goToPreviousSlide: () => void;
  togglePause: () => void;
  setMousePaused: (paused: boolean) => void;
  markSlideComplete: (slideId: RewindSlideId) => void;
  // Timeline sync
  currentTimelinePosition: number;
  totalDuration: number;
  getTimelinePositionForSlide: (slideIndex: number) => number;
};

const DEFAULT_SLIDE_DURATION_MS = 18000; // 18 seconds default
const PROGRESS_UPDATE_INTERVAL_MS = 100; // Update 10 times/second

// Custom duration configuration for each slide (in milliseconds)
// prettier-ignore
const SLIDE_DURATIONS: Partial<Record<RewindSlideId, number>> = {
  opening: 17000,
  journey: 18000,
  impact: 18000,
  style: 19500,
  wins: 22000,
  closing: 20000,
};

function getSlideDuration(slideIndex: number): number {
  const slideId = REWIND_SLIDES[slideIndex];
  return SLIDE_DURATIONS[slideId] ?? DEFAULT_SLIDE_DURATION_MS;
}

/** Calculate total duration of all slides in milliseconds */
function getTotalDuration(): number {
  return REWIND_SLIDES.reduce((total, _, index) => {
    return total + getSlideDuration(index);
  }, 0);
}

/** Get the start time of a specific slide in the total timeline (ms) */
function getSlideStartTime(slideIndex: number): number {
  let startTime = 0;
  for (let i = 0; i < slideIndex; i++) {
    startTime += getSlideDuration(i);
  }
  return startTime;
}

export function useRewindSlides(): UseRewindSlidesReturn {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMousePaused, setIsMousePaused] = useState(false);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [completedSlides, setCompletedSlides] = useState<Set<RewindSlideId>>(
    () => new Set(),
  );

  // Combined pause state - paused when either mouse is held or user explicitly paused
  const isPaused = isMousePaused || isUserPaused;

  const currentSlideId = REWIND_SLIDES[currentSlideIndex];
  const isFirstSlide = currentSlideIndex === 0;
  const isLastSlide = currentSlideIndex === REWIND_SLIDES.length - 1;
  const currentSlideDuration = getSlideDuration(currentSlideIndex);
  const totalDuration = useMemo(() => getTotalDuration(), []);

  // Calculate current timeline position in milliseconds
  const currentTimelinePosition = useMemo(() => {
    const slideStartTime = getSlideStartTime(currentSlideIndex);
    const progressInMs = (progress / 100) * currentSlideDuration;
    return slideStartTime + progressInMs;
  }, [currentSlideIndex, progress, currentSlideDuration]);

  const goToSlide = useCallback(
    (index: number, progressPercent: number = 0) => {
      if (index >= 0 && index < REWIND_SLIDES.length) {
        // If navigating forward, mark the current slide as completed
        if (index > currentSlideIndex) {
          setCompletedSlides((prev) => {
            const newSet = new Set(prev);
            newSet.add(REWIND_SLIDES[currentSlideIndex]);
            return newSet;
          });
        }
        setCurrentSlideIndex(index);
        setProgress(progressPercent);
      }
    },
    [currentSlideIndex],
  );

  // Get the timeline position for a specific slide index (at start of slide)
  const getTimelinePositionForSlide = useCallback((slideIndex: number) => {
    return getSlideStartTime(slideIndex);
  }, []);

  const goToNextSlide = useCallback(() => {
    if (!isLastSlide) {
      // Mark the current slide as completed before moving to next
      setCompletedSlides((prev) => {
        const newSet = new Set(prev);
        newSet.add(REWIND_SLIDES[currentSlideIndex]);
        return newSet;
      });
      setCurrentSlideIndex((prev) => prev + 1);
      setProgress(0);
    }
  }, [isLastSlide, currentSlideIndex]);

  const goToPreviousSlide = useCallback(() => {
    if (!isFirstSlide) {
      setCurrentSlideIndex((prev) => prev - 1);
      setProgress(0);
    }
  }, [isFirstSlide]);

  // Toggle user pause (for play/pause button)
  const togglePause = useCallback(() => {
    setIsUserPaused((prev) => !prev);
  }, []);

  // Set mouse pause (for container mouse hold)
  const setMousePaused = useCallback((paused: boolean) => {
    setIsMousePaused(paused);
  }, []);

  // Mark a specific slide as complete (called by slides when their animations finish)
  const markSlideComplete = useCallback((slideId: RewindSlideId) => {
    setCompletedSlides((prev) => {
      const newSet = new Set(prev);
      newSet.add(slideId);
      return newSet;
    });
  }, []);

  // Auto-advance progress
  useEffect(() => {
    // Pause progress when mouse is held down
    if (isPaused) {
      return;
    }

    const interval = setInterval(() => {
      setProgress((prev) => {
        const increment =
          (PROGRESS_UPDATE_INTERVAL_MS / currentSlideDuration) * 100;
        const newProgress = prev + increment;

        if (newProgress >= 100) {
          if (isLastSlide) {
            // On last slide, just fill to 100% and stop
            return 100;
          }
          goToNextSlide();
          return 0;
        }

        return newProgress;
      });
    }, PROGRESS_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [currentSlideDuration, isLastSlide, isPaused, goToNextSlide]);

  return {
    currentSlideIndex,
    currentSlideId,
    progress,
    isFirstSlide,
    isLastSlide,
    isPaused,
    completedSlides,
    goToSlide,
    goToNextSlide,
    goToPreviousSlide,
    togglePause,
    setMousePaused,
    markSlideComplete,
    // Timeline sync
    currentTimelinePosition,
    totalDuration,
    getTimelinePositionForSlide,
  };
}
